"use client"

import { useState, useEffect } from "react"
import { ArrowUpRight, Loader2, Heart, Calendar, User, UploadCloud, FileText } from "lucide-react"
import { cn } from "@/lib/utils"
import { motion, AnimatePresence } from "framer-motion"
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { useNavigate, useSearchParams } from "react-router-dom"
import { getResources } from "@/lib/resources"
import { usePortal } from "@/hooks/use-portal"
import { toast } from "sonner"
import { downloadFile } from "@/lib/download"

const STATIC_ARTICLES = [
  { title: "Telangana Movement: Key Events & Dates", date: "2 days ago", category: "Editorial" },
  { title: "Economic Survey of AP 2024 Analysis", date: "1 week ago", category: "Analysis" },
  { title: "Modern Indian History: High Yield Topics", date: "2 weeks ago", category: "Syllabus" },
  { title: "Polity: Important Constitutional Articles", date: "3 weeks ago", category: "Primer" }
]

export default function ResourcesSection() {
  const { portal: activePortal } = usePortal()
  const [searchParams] = useSearchParams()
  const activeTabParam = searchParams.get("tab")
  const [activeTab, setActiveTab] = useState(activeTabParam === "magazines" ? "magazines" : "articles")
  const [articles, setArticles] = useState<any[]>([])
  const [pyqs, setPyqs] = useState<any[]>([])
  const [magazines, setMagazines] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  // Sync tab with URL parameter
  useEffect(() => {
    if (activeTabParam === "magazines") {
      setActiveTab("magazines")
      // Force scroll on tab change from URL
      const element = document.getElementById('resources')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else if (activeTabParam === "pyq") {
      setActiveTab("pyq")
      const element = document.getElementById('resources')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else if (activeTabParam === "articles") {
      setActiveTab("articles")
      const element = document.getElementById('resources')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [activeTabParam])

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        if (activeTab === "pyq") {
          const data = await getResources("pyq")
          setPyqs(data.map(d => ({
            ...d,
            date: "Official Archive"
          })))
        } else if (activeTab === "articles") {
          const q = query(collection(db, "articles"), orderBy("createdAt", "desc"), limit(6))
          const snapshot = await getDocs(q)
          setArticles(snapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data(),
            date: doc.data().createdAt?.toDate ? new Date(doc.data().createdAt.toDate()).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : "Recently",
          })))
        } else if (activeTab === "magazines") {
          const q = query(collection(db, "magazines"), orderBy("createdAt", "desc"), limit(6))
          const snapshot = await getDocs(q)
          setMagazines(snapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data(),
            date: `${doc.data().month} ${doc.data().year}`,
          })))
        }
      } catch (error) {
        console.error("Error fetching data:", error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [activeTab])

  const currentItems = (activeTab === "articles" ? articles : activeTab === "pyq" ? pyqs : magazines)
    .filter((item: any) => {
      // Respect the Admin "Hidden" status first
      if (item.isVisible === false) return false;

      // Current Affairs (Articles) and Magazines should ALWAYS be visible irrespective of portal
      if (activeTab === "articles" || activeTab === "magazines") return true;

      // Only filter PYQs by portal keywords/tags
      if (!activePortal) return true;
      
      const pTitle = (item.title || "").toLowerCase();
      const pExam = (item.exam || "").toLowerCase();
      const pPortal = (item.portal || "").toLowerCase();
      
      // Determine if item belongs to TS or AP based on explicit portal field or keywords
      const isItemTS = pPortal === "tspsc" || pPortal === "tspc" || pTitle.includes("tsp") || pExam.includes("tsp") || pTitle.includes("telangana");
      const isItemAP = pPortal === "appsc" || pPortal === "appc" || pTitle.includes("app") || pExam.includes("app") || pTitle.includes("andhra");

      if (activePortal === "tspsc") {
        // If it's explicitly AP and not TS, hide it. 
        // Also if it mentions TSPSC it should be shown.
        if (isItemAP && !isItemTS) return false;
        if (!isItemTS && isItemAP) return false;
        // Default to showing if no specific portal is found, or if it matches TS
        return isItemTS || !isItemAP;
      } else if (activePortal === "appsc") {
        if (isItemTS && !isItemAP) return false;
        if (!isItemAP && isItemTS) return false;
        return isItemAP || !isItemTS;
      }
      
      return true;
    });

  const handleItemClick = (item: any) => {
    // Isolated Magazine Handling
    if (activeTab === "magazines") {
      if (item.pdfUrl || item.link) {
        const url = item.pdfUrl || item.link;
        if (url.includes("firebasestorage")) {
          const toastId = toast.loading("Starting download...");
          downloadFile(url, `${item.title}.pdf`).then(success => {
             if (success) toast.success("Download started", { id: toastId });
             else toast.error("Download failed, opening in tab", { id: toastId });
          });
        } else {
          window.open(url, "_blank");
        }
      } else {
        toast.info("Magazine file coming soon!");
      }
      return;
    }

    // Isolated PYQ Handling
    if (activeTab === "pyq") {
      navigate(`/pyq/${item.slug || item.id}`);
      return;
    }

    // Default Article / Fallback Handling
    if (item.slug) {
      navigate(`/article/${item.slug}`);
    } else {
      navigate('/#courses');
    }
  }

  return (
    <div id="resources" className="relative pt-10 sm:pt-14">
      
      {/* Light Header Area - Above the Blue Box */}
      <div className="max-w-[1280px] mx-auto px-6 sm:px-8 mb-0">
        <div className="flex flex-col items-center text-center mb-8 sm:mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-3 max-w-2xl"
          >
            <div className="flex items-center justify-center gap-4 mb-1">
               <div className="w-10 h-px bg-[#b48c36]/30" />
               <p className="text-[#b48c36] font-bold text-[10px] sm:text-[11px] uppercase tracking-[0.4em]">Study Material</p>
               <div className="w-10 h-px bg-[#b48c36]/30" />
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-[48px] font-serif font-medium tracking-tight text-slate-950 leading-tight mb-8">
              Notes & <br className="hidden sm:block"/> <span className=" font-light text-[#b48c36]">Resources</span>
            </h2>
          </motion.div>

          {/* Academic Ledger Filters */}
          <div className="flex justify-center mb-0 relative z-10 w-full px-4 text-center">
            <div className="inline-flex flex-wrap items-center justify-center bg-white/40 backdrop-blur-md border border-slate-200 p-1 rounded-none gap-1 transition-all duration-700 shadow-2xl shadow-slate-200/20">
              {[
                { id: "articles", label: "Current Affairs" },
                { id: "pyq", label: "PYQs" },
                { id: "magazines", label: "Magazines" }
              ].map((tab) => {
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      "group px-6 sm:px-10 py-3 transition-all duration-500 relative rounded-sm whitespace-nowrap",
                      isActive
                        ? "bg-white shadow-md text-[#b48c36]"
                        : "text-slate-400 hover:text-slate-900 hover:bg-white/40"
                    )}
                  >
                    <span className="text-[10px] sm:text-xs font-serif font-medium uppercase tracking-[0.12em]">
                      {tab.label}
                    </span>
                    
                    {isActive && (
                      <motion.div 
                        layoutId="activeArchiveFilter"
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-[2px] bg-[#b48c36]" 
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Dark Content Part - The "Blue Box" */}
      <section className="relative py-16 lg:pt-24 lg:pb-32 bg-slate-950 overflow-hidden min-h-[500px]">
        {/* Background glassmorphism accents */}
        <div className="absolute top-1/4 -left-20 w-[600px] h-[600px] bg-emerald-900/10 blur-[140px] rounded-full pointer-events-none" />

        <div className="max-w-[1280px] mx-auto px-6 sm:px-8 relative z-10 text-center">
            
            <AnimatePresence mode="wait">
              {isLoading ? (
                <motion.div 
                  key="loader"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-20"
                >
                  <Loader2 className="w-10 h-10 text-[#b48c36] animate-spin mb-4" />
                  <p className="text-[#b48c36] font-bold text-[10px] uppercase tracking-[0.4em]">Retrieving Data...</p>
                </motion.div>
              ) : (
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                {currentItems.length === 0 ? (
                  <div className="col-span-full py-20 text-center">
                    <p className="text-slate-500 font-serif text-lg italic">No items available in this category yet.</p>
                  </div>
                ) : (
                  currentItems.map((item, i) => {
                    const isArticle = activeTab === "articles";
                    const isMagazine = activeTab === "magazines";
                    
                    if (isArticle || isMagazine) {
                      return (
                        <div 
                          key={i} 
                          onClick={() => handleItemClick(item)}
                          className="bg-white rounded-xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] overflow-hidden group hover:shadow-xl transition-all duration-300 flex flex-col text-left cursor-pointer"
                        >
                          <div className="relative aspect-[16/10] overflow-hidden">
                            {item.imageUrl || item.image ? (
                              <img src={item.imageUrl || item.image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="" />
                            ) : (
                              <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center text-slate-300 font-bold uppercase tracking-widest text-[10px]">
                                <FileText size={40} className="mb-2 opacity-20" />
                                <span>Rohith's Academy</span>
                              </div>
                            )}
                            <div className="absolute top-4 left-4">
                              <span className={cn(
                                "text-white text-[8px] font-bold px-3 py-1 rounded-md uppercase tracking-wider",
                                isMagazine ? "bg-slate-900" : "bg-[#b48c36]"
                              )}>
                                {item.category || (isMagazine ? "Journal" : "Editor's Pick")}
                              </span>
                            </div>
                            {isMagazine && (
                              <div className="absolute top-4 right-4 bg-[#b48c36] text-white p-2 rounded-full shadow-lg">
                                 <UploadCloud size={14} />
                              </div>
                            )}
                          </div>
                          <div className="p-5 flex-1 flex flex-col">
                            <div className="flex items-center gap-3 text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">
                              <div className="flex items-center gap-1.5"><Calendar size={11}/> {item.date}</div>
                              <div className="flex items-center gap-1.5"><User size={11}/> {isMagazine ? "HIAS Editorial" : "Team HIAS"}</div>
                            </div>
                            <h3 className="text-lg font-serif font-bold text-slate-950 mb-4 line-clamp-2 leading-snug group-hover:text-[#b48c36] transition-colors">
                              {item.title}
                            </h3>
                            <div className="mt-auto pt-4 border-t border-slate-50">
                              <button className="text-[#b48c36] font-bold text-[10px] uppercase tracking-widest flex items-center gap-2">
                                {isMagazine ? "Get Magazine" : "Read Full Article"} <ArrowUpRight size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    }

                    return (
                      <div 
                        key={i} 
                        onClick={() => handleItemClick(item)}
                        className="group relative p-6 bg-white/[0.03] backdrop-blur-3xl border border-white/10 hover:border-[#b48c36]/40 transition-all duration-500 flex flex-col items-start gap-4 cursor-pointer shadow-2xl hover:-translate-y-2 rounded-2xl text-left border-l-2 border-l-[#b48c36]/20"
                      >
                        
                        {/* Institutional Color Flag */}
                        <div className={cn(
                          "absolute top-6 right-0 w-0.5 h-10 rounded-l-full transition-all duration-500",
                          item.title?.toLowerCase().includes("tspc") || item.exam?.includes("TSPSC") ? "bg-blue-600 shadow-[0_0_15px_rgba(37,99,235,0.4)]" : 
                          item.title?.toLowerCase().includes("appc") || item.exam?.includes("APPSC") ? "bg-emerald-700 shadow-[0_0_15px_rgba(4,120,87,0.4)]" : 
                          "bg-[#b48c36]"
                        )} />

                        <div className="w-full flex justify-between items-start">
                           <div className="flex flex-col gap-1">
                              <p className="text-[9px] font-bold text-[#b48c36] uppercase tracking-[0.2em]">{item.exam || item.category || "General"}</p>
                              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest">{item.year || item.date || "Available Library"}</p>
                           </div>
                           <div className="w-8 h-8 rounded-none border border-slate-700 flex items-center justify-center text-slate-400 group-hover:bg-[#b48c36] group-hover:border-[#b48c36] group-hover:text-white transition-all duration-300">
                              <ArrowUpRight size={14} />
                           </div>
                        </div>
                        <div className="aspect-video bg-slate-100 relative overflow-hidden group-hover:brightness-110 transition-all duration-700">
                        </div>
                        <h3 className="text-xl font-serif font-bold text-white leading-snug group-hover:text-[#b48c36] transition-colors duration-300 pr-5">
                          {item.title}
                        </h3>

                        <p className="text-[11px] text-slate-400 line-clamp-2 font-sans font-light leading-relaxed mt-1 group-hover:text-slate-300 transition-colors">
                          {item.description || "The following examination archive is part of Rohith's Academy Institutional Excellence series."}
                        </p>
                      </div>
                    )
                  })
                )}
                </motion.div>
              )}
            </AnimatePresence>
        </div>
      </section>

    </div>
  )
}


